import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * The screens, each in its own chunk, each fetched before it is asked for.
 *
 * Splitting a route is easy and, done plainly, makes the app worse: the chunk
 * is fetched at the moment of the tap, so every first visit to a tab costs a
 * round trip and a blank frame while it lands. That trades a cost nobody
 * notices — parsing sixty kilobytes at boot — for one everybody does.
 *
 * So the split here comes with a prefetch. `React.lazy` takes a factory, and
 * calling that factory is what fetches the chunk; `lazy` does not care who
 * calls it or when, and a second call returns the same promise the import
 * machinery already has. That means the chunk can be fetched on the app's own
 * terms — once it has opened and the connection is free — and by the time a
 * tab is tapped the module is in memory and React resolves it without ever
 * showing a fallback.
 *
 * What stays in the main bundle:
 *
 *  - **Home**, because it is the screen the splash opens onto. A chunk for it
 *    would be a second round trip inside the boot it is meant to shorten.
 *  - The shell, the navigation, the toasts and the error boundary, which are
 *    on every screen.
 *
 * The one that matters most is `/mining`: the mine is a drawn scene — figures,
 * rock layers, sprite rigs — and about a third of the app's code, reached by
 * one button on Home. Nobody who never presses it should be parsing it.
 */

/** Every screen here is rendered bare by the router; none of them take props. */
type Page = ComponentType<Record<string, never>>;

/** A lazily-loaded screen that can also be fetched ahead of time. */
type Route = LazyExoticComponent<Page> & {
  /** Fetches the chunk now. Safe to call repeatedly; only the first fetches. */
  preload: () => Promise<unknown>;
};

function route(load: () => Promise<{ default: Page }>): Route {
  const component = lazy(load) as Route;
  component.preload = () => load().catch(() => undefined);
  return component;
}

/*
 * This codebase exports its screens by name; `lazy` insists on `default`. This
 * is the adapter, and it is also where the prefetch gets its handle: the
 * factory it returns is the same one `lazy` is given, so calling it early is
 * literally the import `lazy` would have done later.
 */
const pick =
  <K extends string>(key: K, load: () => Promise<Record<K, Page>>) =>
  () =>
    load().then((module) => ({ default: module[key] }));

export const MiningPage = route(
  pick('MiningPage', () => import('../features/mining/MiningPage.js')),
);
export const MinersPage = route(
  pick('MinersPage', () => import('../features/mining/MinersPage.js')),
);
export const TasksPage = route(pick('TasksPage', () => import('../features/tasks/TasksPage.js')));
export const ReferralsPage = route(
  pick('ReferralsPage', () => import('../features/referrals/ReferralsPage.js')),
);
export const WalletPage = route(
  pick('WalletPage', () => import('../features/wallet/WalletPage.js')),
);
export const ActivityPage = route(
  pick('ActivityPage', () => import('../features/activity/ActivityPage.js')),
);
export const ProfilePage = route(
  pick('ProfilePage', () => import('../features/profile/ProfilePage.js')),
);
export const ComboPage = route(pick('ComboPage', () => import('../features/combo/ComboPage.js')));
export const RoulettePage = route(
  pick('RoulettePage', () => import('../features/roulette/RoulettePage.js')),
);

/**
 * In the order someone is likely to reach them.
 *
 * The bottom navigation first, because that is one tap from Home and it is
 * where most people go; the mine after it; the roulette and the combo last,
 * since both are reached from a card on Home that has to be scrolled to.
 */
const ALL: Route[] = [
  MinersPage,
  WalletPage,
  TasksPage,
  ReferralsPage,
  ProfilePage,
  MiningPage,
  ActivityPage,
  ComboPage,
  RoulettePage,
];

/**
 * Fetches every screen's chunk in the background, once the app has opened.
 *
 * Deliberately after first paint and inside an idle callback: these are tens
 * of kilobytes against megabytes of artwork on the same connection, and the
 * artwork is what the person is actually looking at. Being late here costs
 * nothing, because the fallback is the chunk arriving on demand — which is
 * what an unsplit lazy route does anyway.
 */
export function preloadRoutes(): void {
  const run = () => {
    for (const screen of ALL) void screen.preload();
  };
  const idle = (globalThis as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (idle) idle(run);
  else setTimeout(run, 1_500);
}
